import type { AnswerResponse, PreviewResult, Question } from '@tabel/shared/schemas/questions';
import { CircleAlert, ListChecks, TriangleAlert } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { api } from '../lib/api';
import { ApiError } from '../lib/errors';
import { type HoursFormat, hoursText } from '../lib/format';
import {
  bulkLabel,
  dayLabel,
  factShort,
  nq,
  staleQuestions,
  typeKind,
  typeLabel,
  warningText,
} from '../lib/questions';
import { type ToastAction, errorText, useToast } from './ErrorToast';
import {
  AnswerFields,
  AnswerOptions,
  BlockReason,
  type DraftValues,
  answerBlockReason,
  timeLabelOf,
  useAnswerForm,
  useAnswerKeys,
} from './QuestionBlock';
import { UnsavedMark, useUnsavedChanges, useUnsavedGuard } from './UnsavedGuard';
import { Button, SidePanel } from './ui';

/*
 * Ответ сразу нескольким — режим правой панели (003, US2, сценарий 5; FR-021; contracts/ui-panel.md, «Режимы правой
 * панели»): один ответ на выбранные вопросы одного типа. Часы каждого человека считает сервер и показывает до
 * сохранения (предпросмотр пачки); ошибка у одного человека блокирует «Сохранить для N», предупреждение — нет.
 * Ответ — несохранённое («Сохранить ответ?»). Запись — одна пачка с общим `batch_id` на сервере.
 */

const SCREEN = 'Timesheet';
const CAPTION = 'text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase';

/** Вопрос для всех сразу простыми словами. */
function groupText(type: string): string {
  switch (typeKind(type)) {
    case 'нет_выхода':
      return 'У этих людей есть вход и нет выхода. Когда был выход?';
    case 'нет_входа':
      return 'У этих людей есть выход и нет входа. Когда был вход?';
    default:
      return 'Один ответ для всех выбранных вопросов.';
  }
}

const shown = (r: PreviewResult | undefined, f: HoursFormat | null) =>
  r?.value ?? (r?.hours != null ? hoursText(r.hours, f) : '—');

export interface BulkAnswerProps {
  open: boolean;
  onClose: () => void;
  /** «Назад» и «Отмена»: к списку вопросов; нет — закрыть панель. */
  onBack?: () => void;
  timesheetId: number;
  timesheetName: string;
  /** Выбранные вопросы одного типа, в порядке списка. */
  questions: readonly Question[];
  format: HoursFormat | null;
  /** Ответ записан: страница обновляет вопросы и перечитывает табель. */
  onSaved: (res: AnswerResponse, count: number) => void;
  /** Вопрос снят или изменился: текущие вопросы из `409 STALE_STATE`; страница перечитывает данные. */
  onStale?: (current: Question[]) => void;
  returnFocus?: () => HTMLElement | null | undefined;
  /** «Отменить» в тосте по ответу сервера (`undoActionId`, 003 FR-038); нет — тост без кнопки. */
  undo?: (res: unknown) => ToastAction | undefined;
}

export function BulkAnswer({
  open,
  onClose,
  onBack,
  timesheetId,
  timesheetName,
  questions,
  format,
  onSaved,
  onStale,
  returnFocus,
  undo,
}: BulkAnswerProps) {
  const { showError, showInfo } = useToast();
  const { clearDirty } = useUnsavedGuard();
  const [busy, setBusy] = useState(false);
  const first = questions[0];
  const n = questions.length;
  const options = first?.options ?? [];
  const initial: DraftValues = { option: options[0]?.key ?? null, hours: '', time: '', note: '' };
  const form = useAnswerForm({
    timesheetId,
    formKey: questions.map((q) => q.id).join(','),
    items: questions.map((q) => ({ questionId: q.id, version: q.version })),
    initial,
    format,
    enabled: open && n > 0,
  });
  const canSave = open && n > 0 && form.ready && !busy;
  // Почему «Сохранить для N» неактивна — словами рядом с кнопкой (как в карточке дня).
  const reason = open && first && !busy ? answerBlockReason(form, timeLabelOf(first.type, true)) : null;
  const back = onBack ?? onClose;

  /** `viaGuard` — из окна «Сохранить ответ?»: расчёт мог ещё не прийти, сервер проверит ответ сам. */
  const submit = async (viaGuard = false) => {
    const req = form.request;
    if (!req || !(viaGuard ? form.complete : form.ready)) throw new Error('Ответ не дописан');
    setBusy(true);
    try {
      const res = await api.answer(timesheetId, {
        items: questions.map((q) => ({ questionId: q.id, version: q.version })),
        answer: req,
      });
      form.reset();
      clearDirty();
      showInfo(req.option === 'later' ? `Отложено: ${nq(n)}` : `Ответ сохранён: ${nq(n)}`, {
        action: undo?.(res),
      });
      onSaved(res, n);
    } catch (e) {
      const changed = e instanceof ApiError && (e.code === 'QUESTION_GONE' || e.code === 'STALE_STATE');
      showError(e, {
        screen: SCREEN,
        action: 'answerBulk',
        lead: 'Не сохранено',
        note: changed ? 'Список обновлён: проверьте выбор.' : 'Ответ остался в панели.',
        retry: changed ? undefined : () => void latest.current().catch(() => {}),
      });
      if (changed) onStale?.(staleQuestions(e.details));
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const latest = useRef(submit);
  latest.current = submit;

  const label = (o: (typeof options)[number]) => bulkLabel(o.key, first?.type ?? '', o.label);
  const chosen = options.find((o) => o.key === form.values.option);
  useUnsavedChanges(
    open && first && form.dirty
      ? {
          kind: 'bulk',
          summary: `${typeLabel(first.type)} — ${nq(n)}: ${chosen ? label(chosen).replace(/\s*…\s*$/, '') : 'ответ не выбран'}${form.request?.option === 'time' ? ` ${form.request.time}` : ''}`,
          canSave: form.complete,
          save: () => latest.current(true),
          discard: form.reset,
        }
      : null,
  );
  const keys = useAnswerKeys({
    enabled: open && n > 0,
    options,
    onPick: form.setOption,
    onSave: () => {
      if (canSave) void submit().catch(() => {});
    },
  });

  // «В табель попадёт»: часы каждого человека — от сервера.
  let preview: ReactNode = null;
  const v = form.values;
  const box = 'flex flex-col gap-1.5 rounded-control border px-3.5 py-3 text-[13px]';
  if (!v.option) preview = <div className={`${box} border-line bg-subtle text-ink-2`}>Выберите ответ.</div>;
  else if (v.option === 'later')
    preview = (
      <div className={`${box} border-line bg-subtle text-ink-2`}>
        <span className={CAPTION}>Что будет</span>
        <span>Вопросы останутся без ответа и в счётчике.</span>
      </div>
    );
  else if (v.option === 'hours' && !v.hours.trim())
    preview = (
      <div className={`${box} border-line bg-subtle text-ink-2`}>
        Укажите часы — здесь появится расчёт по каждому.
      </div>
    );
  else if (v.option === 'time' && !v.time.trim())
    preview = (
      <div className={`${box} border-line bg-subtle text-ink-2`}>
        Укажите время — здесь появится расчёт по каждому.
      </div>
    );
  else if (form.hoursError || (v.option === 'time' && !form.request)) preview = null;
  else if (form.preview.status === 'fail') {
    const text = errorText(form.preview.error);
    preview = (
      <div className={`${box} border-transparent bg-bad-soft text-bad`}>
        <span>Не удалось посчитать: {text}.</span>
        <span>
          <Button size="sm" onClick={form.retryPreview}>
            Повторить
          </Button>
        </span>
      </div>
    );
  } else if (form.preview.status !== 'ok')
    preview = <div className={`${box} border-line bg-subtle text-ink-3`}>Считаем…</div>;
  else {
    const results = form.preview.results;
    const byId = (id: number) => results.find((r) => r.questionId === id);
    const bad = questions.filter((q) => byId(q.id)?.ok === false);
    const edit = v.option !== 'accept';
    // Ответ временем: у каждого свой отрезок «вход — выход» и свои часы — по расчёту сервера.
    const timed = v.option === 'time';
    const spanOf = (q: Question) => {
      const s = byId(q.id)?.span;
      return s ? `${s.from} — ${s.to}${s.nextDay ? ' (след. день)' : ''}` : factShort(q);
    };
    preview = bad.length ? (
      <div className={`${box} border-transparent bg-bad-soft text-bad`}>
        {bad.map((q) => (
          <span key={q.id} className="flex items-start gap-2">
            <CircleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
            <span>
              {q.displayName}: {byId(q.id)?.error?.message ?? 'ответ не подходит'}
            </span>
          </span>
        ))}
      </div>
    ) : (
      <div className={`${box} border-line bg-surface`}>
        <span className={CAPTION}>В табель попадёт</span>
        <table aria-label="В табель попадёт" className="w-full table-fixed text-[13px]">
          <thead className="text-left text-[12px] text-ink-3">
            <tr>
              <th className="w-[38%] py-1 pr-2 font-medium">Сотрудник</th>
              <th className="py-1 pr-2 font-medium">{timed ? 'Вход — выход' : 'Проходы'}</th>
              <th className="w-16 py-1 text-right font-medium">Часы</th>
            </tr>
          </thead>
          <tbody>
            {questions.map((q) => (
              <tr key={q.id} className="border-t border-line align-top">
                <td className="py-1.5 pr-2 break-words">{q.displayName}</td>
                <td className="py-1.5 pr-2 break-words text-ink-2 tabular-nums">
                  {timed ? spanOf(q) : factShort(q)}
                </td>
                <td className="py-1.5 text-right whitespace-nowrap tabular-nums">
                  <b className="font-semibold">{shown(byId(q.id), format)} ч</b>
                  {edit ? ' ✎' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {questions
          .flatMap((q) => (byId(q.id)?.warnings ?? []).map((w) => ({ q, w })))
          .slice(0, 3)
          .map(({ q, w }) => (
            <span
              key={`${q.id}-${w}`}
              className="flex items-start gap-2 rounded-[6px] bg-warn-soft px-2.5 py-1.5 text-ink"
            >
              <TriangleAlert size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-warn" aria-hidden />
              <span>
                {q.displayName}: {warningText(w)}
              </span>
            </span>
          ))}
        {results[0]?.comment && (
          <>
            <span className={`${CAPTION} mt-1`}>
              {edit ? 'Комментарий в каждой ячейке' : 'Запись в журнале'}
            </span>
            <span className="rounded-[6px] bg-subtle px-2.5 py-1.5 text-ink">{results[0].comment}</span>
          </>
        )}
      </div>
    );
  }

  const dates = [...new Set(questions.map((q) => dayLabel(q.date)))].join(', ');
  return (
    <SidePanel
      open={open}
      onClose={onClose}
      onBack={onBack}
      label="Ответ сразу нескольким"
      crumb={timesheetName}
      title={`Ответ сразу на ${nq(n)}`}
      icon={ListChecks}
      returnFocus={returnFocus}
      footer={
        <>
          <span className="flex-1" aria-hidden />
          {reason ? <BlockReason text={reason} /> : form.dirty && <UnsavedMark />}
          <Button disabled={busy} onClick={back}>
            Отмена
          </Button>
          <Button variant="primary" disabled={!canSave} onClick={() => void submit().catch(() => {})}>
            Сохранить для {n}
          </Button>
        </>
      }
    >
      {first ? (
        <>
          <div>
            <h2 className="text-[19px] leading-[1.3] font-semibold tracking-[-0.015em] text-ink">
              {typeLabel(first.type)}: {nq(n)}
            </h2>
            <p className="mt-0.5 text-[13px] break-words text-ink-2">
              {dates} · {questions.map((q) => q.displayName).join(', ')}
            </p>
          </div>
          <p className="text-[14px] leading-[1.45] text-ink">{groupText(first.type)}</p>
          <AnswerOptions
            options={options}
            value={v.option}
            onChange={form.setOption}
            keys={keys}
            labelOf={label}
          />
          {v.option && (
            <AnswerFields
              form={form}
              hoursLabel="Часы каждому"
              timeLabel={timeLabelOf(first.type, true)}
              timeHint="Одно время для всех. Например, 19:00 или 1900"
              notePlaceholder="Например: не работал турникет выхода"
            />
          )}
          <div aria-live="polite">{preview}</div>
        </>
      ) : (
        <p className="text-[13px] text-ink-3">Вопросы не выбраны.</p>
      )}
    </SidePanel>
  );
}
